import {DEFAULT_PATH} from './APIPath'

interface RequestHeaders {
  [key: string]: string;
}

export class AuthError extends Error {
    constructor(message: string, public statusCode?: number) {
        super(message);
        this.name = 'AuthError';
    }
}

/**
 * Retrieves the authorization token from local storage.
 * @returns The token string or null if not found.
 */
function getAuthToken(): string | null {
  return localStorage.getItem("token");
}

/**
 * Handles common response logic
 * checking for authentication errors (401, 403).
 * @param response The fetch Response object.
 * @returns The parsed JSON data.
 * @throws An error if the response is not ok or for specific auth errors.
 */
async function handleApiResponse(response: Response): Promise<any> {
  if (response.ok) {
    try {
      return response;
    } catch (e) {
      throw new Error ("API response was not ok. Error: " + e);
    }
  } else {
    const errorBody = await response.text();
    let errorMessage = `API Error: ${response.status} ${response.statusText}`;
    let errorData: any = null;

    try {
      errorData = JSON.parse(errorBody);
      errorMessage = errorData.message || errorMessage;
    } catch (e) {
      errorMessage = errorBody || errorMessage;
    }

    if (response.status === 401) {
      console.error(`Auth Error (${response.status}): ${errorMessage}.`);
      throw new AuthError("Authentication or authorization failed.", response.status);
    } else if (response.status === 403) {
      console.error(`Auth Error (${response.status}): ${errorMessage}.`);
      const unauthorizedRoleMessage = "Unauthorized role.";
      const insufficientPermissionsMessage = "Insufficient permissions."

      if (errorMessage === unauthorizedRoleMessage) {
        console.error(`Auth Error (403 Forbidden - Role Demoted): ${errorMessage}. Clearing token and redirecting to login.`);
        localStorage.removeItem("token");
        window.location.href = '/login';
        throw new AuthError(errorMessage, response.status);

      } else if (errorMessage === insufficientPermissionsMessage) {
        console.warn(`Auth Error (403 Forbidden - Insufficient Permissions): ${errorMessage}.`);
        throw new AuthError(errorMessage, response.status);
      } else {
        console.error(`Auth Error (403 Forbidden - Unknown Reason): ${errorMessage}.`);
        throw new AuthError(errorMessage, response.status);
      }

    }

    throw new Error(errorMessage);
  }
}

/**
 * Base fetch wrapper.
 * @param apiPath The API endpoint path.
 * @param method The HTTP method (GET, POST, etc.).
 * @param body Optional request body.
 * @param headers Optional custom headers.
 * @returns The parsed JSON response.
 */
async function baseFetch(
  apiPath: string,
  method: string,
  body?: any,
  headers?: RequestHeaders
): Promise<any> {

  const apiHeaders = new Headers(headers);

  const requestOptions: RequestInit = {
    method: method,
    headers: apiHeaders,
  };


  if (body && ['POST', 'PUT', 'PATCH', 'DELETE'].includes(method.toUpperCase())) {
    requestOptions.body = JSON.stringify(body);

    if (!apiHeaders.has('Content-Type')) {
      apiHeaders.set('Content-Type', 'application/json');
    }
  }

  try {
    const apiResponse = await fetch(DEFAULT_PATH + apiPath, requestOptions);
    return await handleApiResponse(apiResponse);
  } catch (error) {
    console.error(`Network or fetch error for ${method} ${apiPath}:`, error);
    throw error;
  }
}

/**
 * Returns common authenticated headers.
 * @param additionalHeaders Optional additional headers to merge.
 * @returns Combined headers, including Authorization.
 * @throws An error if no token is found for an authenticated request.
 */
function getAuthenticatedHeaders(additionalHeaders: RequestHeaders = {}): RequestHeaders {
  const token = getAuthToken();
  if (!token) {

    console.error("No token found for authenticated request. Redirecting to login.");
    throw new AuthError("Authentication token missing.")
  }

  return {
    ...additionalHeaders,
    'Authorization': `Bearer ${token}`,
  };
}

// --- Public API Functions ---

export async function get(apiPath: string, headers?: RequestHeaders): Promise<any> {

  return await baseFetch(apiPath, 'GET', undefined, headers);
}

export async function getAuth(apiPath: string, headers?: RequestHeaders): Promise<any> {
  const authHeaders = getAuthenticatedHeaders(headers);
  return await get(apiPath, authHeaders);
}

export async function post(apiPath: string, body?: any, headers?: RequestHeaders): Promise<any> {
  return await baseFetch(apiPath, 'POST', body, headers);
}

export async function postAuth(apiPath: string, body?: any, headers?: RequestHeaders): Promise<any> {
  const authHeaders = getAuthenticatedHeaders(headers);
  return await post(apiPath, body, authHeaders);
}

export async function put(apiPath: string, body?: any, headers?: RequestHeaders): Promise<any> {
  return await baseFetch(apiPath, 'PUT', body, headers);
}

export async function putAuth(apiPath: string, body?: any, headers?: RequestHeaders): Promise<any> {
  const authHeaders = getAuthenticatedHeaders(headers);
  return await put(apiPath, body, authHeaders);
}

export async function del(apiPath: string, body?: any, headers?: RequestHeaders): Promise<any> {
  return await baseFetch(apiPath, 'DELETE', body, headers);
}

export async function delAuth(apiPath: string, body?: any, headers?: RequestHeaders): Promise<any> {
  const authHeaders = getAuthenticatedHeaders(headers);
  return await del(apiPath, body, authHeaders);
}